import { isMessage } from '@bufbuild/protobuf';
import {
  Creative,
  CreativeContext,
  CreativeSchema,
  Creative_Status,
  MediaType,
} from '@sites/data-hmm/hmm-incubator';

export interface PreviewCreative {
  id: string;
  name: string;
  formatName: string;
  type: MediaType;
  duration: string;
  creativeConfidential: boolean;
  recordingUrl?: string;
  thumbnailUrl?: string;
  gifUrl?: string;
  /** MP4 hover preview; played instead of the GIF when it loads. */
  previewUrl?: string;
  state: RecordingStatus;
}

export type RecordingStatus =
  | 'invisible'
  | 'pending'
  | 'notLocked'
  | 'recorded';

export type PreviewMode = 'standard' | 'silent' | 'audio' | 'raw' | 'rawSilent';

export const isRawMode = (mode: PreviewMode | undefined): boolean =>
  mode === 'raw' || mode === 'rawSilent';

export const getPreviewCreative = (
  creative: Creative | CreativeContext,
  thumbnailUrl: string | undefined,
  gifUrl: string | undefined,
  recordingUrl: string | undefined,
  previewUrl?: string
): PreviewCreative => {
  if (isMessage(creative, CreativeSchema)) {
    return {
      id: creative.id,
      name: creative.name,
      formatName: creative.creativeFormatName.split('_').join(' '),
      type: creative.mediaType,
      duration:
        creative.mediaType === MediaType.VIDEO
          ? `${creative.durationMs / 1000} s`
          : 'N/A',
      creativeConfidential: creative.applyWatermark,
      recordingUrl: recordingUrl ?? creative.recording,
      thumbnailUrl: thumbnailUrl ?? creative.thumbnail,
      gifUrl: gifUrl ?? creative.gif,
      previewUrl: previewUrl ?? creative.recordingPreview,
      state: setNoRecordingTooltipMessage(
        creative.creativeFormatName,
        creative.status,
        creative.thumbnail
      ),
    };
  } else {
    return {
      id: creative.creativeId,
      name: creative.creativeName,
      formatName: creative.creativeFormatName.split('_').join(' '),
      type: creative.creativeMediaType,
      duration:
        creative.creativeMediaType === MediaType.VIDEO
          ? `${creative.creativeDurationMs / BigInt(1000)} s`
          : 'N/A',
      creativeConfidential: creative.creativeApplyWatermark,
      recordingUrl: recordingUrl ?? creative.creativeRecordingUrl,
      thumbnailUrl: thumbnailUrl ?? creative.creativeThumbnailUrl,
      gifUrl: gifUrl ?? creative.creativeGifUrl,
      previewUrl: previewUrl ?? creative.creativePreviewUrl,
      state: setNoRecordingTooltipMessage(
        creative.creativeFormatName,
        creative.creativeStatus,
        creative.creativeThumbnailUrl
      ),
    };
  }
};

const setNoRecordingTooltipMessage = (
  creativeFormatName: string,
  status: Creative_Status,
  thumbnailUrl: string
): RecordingStatus => {
  if (creativeFormatName === 'invisible') {
    return 'invisible';
  } else if (!thumbnailUrl) {
    if (status === Creative_Status.LOCKED) {
      return 'pending';
    } else {
      return 'notLocked';
    }
  }
  return 'recorded';
};
